import { useTranslation } from 'react-i18next'
import { Coins } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PublicProduct } from '@/types/checkout'
import { pointsUsable, pricingModeOf } from './productPoints'
import { usePointsLabel } from './usePointsName'

/**
 * F-160: the points side of a product's price. Points-only: "800 pontos" as the price; points or money:
 * "ou 800 pontos" under the money price. What the viewer still misses, from the server, under it.
 */
export function PointsPrice({ product, className, size = 'md' }: { product: PublicProduct; className?: string; size?: 'md' | 'lg' }) {
  const { t } = useTranslation('points')
  const label = usePointsLabel()
  const mode = pricingModeOf(product)
  const price = Number(product.pointsPrice) || 0
  if (mode === 'MONEY' || price <= 0) return null
  const shortfall = Number(product.pointsShortfall) || 0

  return (
    <div className={cn('min-w-0', className)} data-testid="product-points-price">
      {mode === 'POINTS' ? (
        <p className={cn('flex items-center gap-1.5 font-semibold text-foreground', size === 'lg' ? 'text-2xl' : 'text-lg')}>
          <Coins className={size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />{label(price)}
        </p>
      ) : (
        <p className="flex items-center gap-1.5 text-sm text-foreground-secondary"><Coins className="h-3.5 w-3.5" />{t('card.orPoints', { points: label(price) })}</p>
      )}
      {!pointsUsable(product) ? (
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="product-points-off">{t('card.unavailable')}</p>
      ) : shortfall > 0 ? (
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="product-points-shortfall">{t('card.shortfall', { points: label(shortfall) })}</p>
      ) : null}
    </div>
  )
}
